import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  IconCalendarEvent, IconBuildingWarehouse, IconUsers, IconCheck,
  IconX, IconEdit, IconTrash, IconSearch, IconFileText, IconPrinter,
  IconDownload, IconFileInvoice, IconPlus, IconRefresh, IconClock,
  IconAlertTriangle, IconAlertCircle, IconHanger, IconBrush,
  IconDeviceDesktop, IconInfoCircle, IconListCheck, IconEye,
  IconArrowsExchange, IconChevronRight, IconTrendingUp, IconCash, IconToolsKitchen2
} from "@tabler/icons-react";
import apiClient from "../helpers/ApiClient";
import * as eventsApi from "../controllers/events.controller";
import { useStoreSettings } from "../controllers/settings.controller";

export default function EventsPage() {
  const { data: storeSettings } = useStoreSettings();
  const currency = storeSettings?.currency || "Rs";

  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState(urlTab || "dashboard"); // dashboard, events, calendar, venues, packages, reports
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (urlTab) {
      setTab(urlTab);
    }
  }, [urlTab]);

  // Core Data Lists
  const [eventsList, setEventsList] = useState([]);
  const [venues, setVenues] = useState([]);
  const [packages, setPackages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [calendarFeed, setCalendarFeed] = useState({ events: [], functions: [] });
  const [reportsData, setReportsData] = useState(null);

  // Active / Selected Event Workspace
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventDetails, setEventDetails] = useState(null);
  const [workspaceTab, setWorkspaceTab] = useState("overview"); // overview, quotations, beo, tasks, checklist, payments, amendments

  // Quotations List & Filter State
  const [quotationsList, setQuotationsList] = useState([]);
  const [quotationSearchQuery, setQuotationSearchQuery] = useState("");
  const [quotationStatusFilter, setQuotationStatusFilter] = useState("all");
  const [viewingQuotation, setViewingQuotation] = useState(null);

  const [directQuotationForm, setDirectQuotationForm] = useState({
    event_id: "",
    customer_name: "", customer_phone: "", customer_email: "", customer_company: "",
    customer_address: "", customer_pan: "", event_title: "", event_type: "Wedding",
    primary_venue_id: "", package_id: "",
    start_date: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
    end_date: new Date(Date.now() + 86400000 + 6 * 3600000).toISOString().slice(0, 16),
    guest_count: 100, seating_layout: "Round Table",
    discount_percent: 0, tax_percent: 13, advance_required_percent: 25,
    valid_until: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
    terms_conditions: "1. 25% non-refundable advance required to confirm venue booking.\n2. Final guest count must be confirmed 72 hours prior.\n3. Outside catering and alcohol subject to prior approval.\n4. Standard banquet halls and audio-visual guidelines apply.",
    items: [
      { item_type: "food_beverage", name: "Banquet Buffet Menu Package", quantity: 100, unit: "Guests", unit_price: 1200, tax_percent: 13, notes: "" },
      { item_type: "venue_rental", name: "Banquet Hall Rental Fee", quantity: 1, unit: "Day", unit_price: 15000, tax_percent: 13, notes: "Includes standard hall setup" }
    ]
  });

  // Quotation Calculations for Direct Creation Form
  const directQuotationCalculations = useMemo(() => {
    const items = directQuotationForm.items || [];
    let subtotal = 0;
    items.forEach(it => {
      subtotal += (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
    });
    const discPct = Number(directQuotationForm.discount_percent) || 0;
    const discountAmount = (subtotal * discPct) / 100;
    const taxable = Math.max(0, subtotal - discountAmount);
    const taxPct = Number(directQuotationForm.tax_percent) || 0;
    const taxAmount = (taxable * taxPct) / 100;
    const grandTotal = taxable + taxAmount;
    const advPct = Number(directQuotationForm.advance_required_percent) || 0;
    const advanceAmount = (grandTotal * advPct) / 100;
    return {
      subtotal,
      discountAmount,
      taxable,
      taxAmount,
      grandTotal,
      advanceAmount
    };
  }, [directQuotationForm]);

  // Filtered Quotations
  const filteredQuotations = useMemo(() => {
    return (quotationsList || []).filter(q => {
      if (quotationStatusFilter !== "all" && q.status !== quotationStatusFilter) {
        return false;
      }
      if (quotationSearchQuery) {
        const sq = quotationSearchQuery.toLowerCase();
        const matchNumber = String(q.quotation_number || "").toLowerCase().includes(sq);
        const matchCustomer = String(q.customer_name || "").toLowerCase().includes(sq);
        const matchPhone = String(q.customer_phone || "").toLowerCase().includes(sq);
        const matchEvent = String(q.event_title || "").toLowerCase().includes(sq);
        if (!matchNumber && !matchCustomer && !matchPhone && !matchEvent) return false;
      }
      return true;
    });
  }, [quotationsList, quotationStatusFilter, quotationSearchQuery]);

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  // "new_inquiry", "check_availability", "add_venue", "edit_venue", "add_package", "edit_package",
  // "new_quotation", "add_payment", "cancel_event", "add_task", "issue_linen", "add_charge", "add_function"

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [venueFilter, setVenueFilter] = useState("all");
  const [reportDateRange, setReportDateRange] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10)
  });

  // Forms
  const [inquiryForm, setInquiryForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "", customer_company: "",
    customer_address: "", customer_pan: "", event_title: "", event_type: "Wedding",
    primary_venue_id: "", package_id: "", start_date: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
    end_date: new Date(Date.now() + 86400000 + 6 * 3600000).toISOString().slice(0, 16),
    expected_guests: 100, guaranteed_guests: 100, seating_layout: "Round Table",
    estimated_budget: 0, sales_owner: "", follow_up_date: "", follow_up_notes: "", special_requirements: ""
  });

  const [venueForm, setVenueForm] = useState({
    name: "", code: "", venue_type: "Banquet Hall", capacity_min: 20, capacity_max: 300,
    rental_rate_per_day: 5000, rental_rate_per_hour: 500, setup_buffer_mins: 60,
    teardown_buffer_mins: 60, is_exclusive: true, description: "", amenities: "AC, Sound System, Stage, Projector"
  });

  const [packageForm, setPackageForm] = useState({
    name: "", package_type: "per_person", base_price: 1200, min_guests: 50,
    description: "", inclusions: ["Welcome Mocktail", "3 Veg Starters", "2 Non-Veg Starters", "Main Buffet", "2 Desserts"]
  });

  const [availabilityForm, setAvailabilityForm] = useState({
    venue_id: "", start_time: new Date().toISOString().slice(0, 16),
    end_time: new Date(Date.now() + 6 * 3600000).toISOString().slice(0, 16)
  });
  const [availabilityResult, setAvailabilityResult] = useState(null);

  const [quotationForm, setQuotationForm] = useState({
    guest_count: 100, discount_percent: 0, tax_percent: 13, advance_required_percent: 25,
    valid_until: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
    terms_conditions: "Standard banquet terms apply. 25% non-refundable advance deposit required to confirm venue.",
    items: [
      { item_type: "food_beverage", name: "Buffet Menu Package", quantity: 100, unit: "Guests", unit_price: 1200, tax_percent: 13, notes: "" },
      { item_type: "venue_rental", name: "Hall Rental Fee", quantity: 1, unit: "Days", unit_price: 15000, tax_percent: 13, notes: "Includes standard setup" },
      { item_type: "decoration", name: "Floral Backdrop & Stage Lighting", quantity: 1, unit: "Set", unit_price: 8000, tax_percent: 13, notes: "" }
    ]
  });

  const [beoForm, setBeoForm] = useState({
    theme_color: "Gold & Royal Blue", stage_setup: "Elevated stage with podium and wireless mics",
    seating_instructions: "Round tables of 8 with floral centerpiece",
    av_requirements: "Dual HD Projectors, 4 Cordless Mics, DJ Audio Console",
    food_service_style: "Buffet", dietary_requirements: "10 Jain Vegetarian, 5 Vegan",
    bar_service_notes: "Non-Alcoholic Welcome Drinks & Soft Beverages",
    signage_instructions: "Display event board at Grand Entrance Lobby",
    special_instructions: "Guest of honor arriving at 06:30 PM",
    menus: [
      { course_name: "Welcome Drinks", menu_items: "Mint Mojito, Blue Lagoon, Fresh Orange", service_time: "06:00 PM - 07:00 PM", portion_count: 100, dietary_notes: "" },
      { course_name: "Starters & Canapes", menu_items: "Paneer Tikka, Chicken Seekh, Veg Spring Rolls", service_time: "06:30 PM - 08:00 PM", portion_count: 100, dietary_notes: "" },
      { course_name: "Main Course Buffet", menu_items: "Butter Chicken, Paneer Butter Masala, Dal Makhani, Jeera Rice, Assorted Naan", service_time: "08:30 PM - 10:30 PM", portion_count: 100, dietary_notes: "" },
      { course_name: "Desserts", menu_items: "Gulab Jamun with Vanilla Ice Cream, Fruit Salad", service_time: "09:30 PM - 11:00 PM", portion_count: 100, dietary_notes: "" }
    ]
  });

  const [paymentForm, setPaymentForm] = useState({
    amount: 1000, payment_type: "advance_deposit", payment_method: "cash",
    payment_date: new Date().toISOString().slice(0, 16), reference_no: "", notes: ""
  });

  const [taskForm, setTaskForm] = useState({
    department: "Kitchen", task_name: "", description: "", assigned_to: "", due_time: ""
  });

  const [linenForm, setLinenForm] = useState({
    venue_name: "", items: [{ item_id: "", name: "", quantity: 20 }]
  });

  const [chargeForm, setChargeForm] = useState({
    charge_amount: 500, description: "Additional 10 Guests Buffet & Drinks"
  });

  const [cancelForm, setCancelForm] = useState({
    cancellation_reason: "Customer cancelled due to personal reasons",
    cancellation_fee: 0, refund_method: "bank_transfer"
  });

  const [functionForm, setFunctionForm] = useState({
    function_name: "Evening Cocktail & Dinner", venue_id: "",
    start_time: new Date().toISOString().slice(0, 16),
    end_time: new Date(Date.now() + 4 * 3600000).toISOString().slice(0, 16),
    expected_guests: 100, seating_layout: "Round Table", meal_style: "Buffet", notes: ""
  });

  useEffect(() => {
    fetchTabData();
  }, [tab]);

  useEffect(() => {
    fetchAuxiliaryData();
  }, []);

  const fetchAuxiliaryData = async () => {
    try {
      const [vRes, pRes, cRes] = await Promise.allSettled([
        eventsApi.getEventVenues(),
        eventsApi.getEventPackages(),
        apiClient.get("/customers")
      ]);
      if (vRes.status === "fulfilled") setVenues(vRes.value || []);
      if (pRes.status === "fulfilled") setPackages(pRes.value || []);
      if (cRes.status === "fulfilled" && cRes.value.data) {
        setCustomers(Array.isArray(cRes.value.data) ? cRes.value.data : cRes.value.data.customers || []);
      }
    } catch (e) {
      console.warn("Auxiliary load error:", e);
    }
  };

  const fetchTabData = async () => {
    setLoading(true);
    try {
      if (tab === "dashboard") {
        const stats = await eventsApi.getEventDashboardStats();
        setDashboardStats(stats);
      } else if (tab === "events") {
        const list = await eventsApi.getEvents({
          status: statusFilter,
          event_type: typeFilter,
          venue_id: venueFilter !== "all" ? venueFilter : undefined,
          search: searchQuery || undefined
        });
        setEventsList(list || []);
      } else if (tab === "calendar") {
        const feed = await eventsApi.getEventCalendarFeed();
        setCalendarFeed(feed || { events: [], functions: [] });
      } else if (tab === "venues") {
        const v = await eventsApi.getEventVenues();
        setVenues(v || []);
      } else if (tab === "packages") {
        const p = await eventsApi.getEventPackages();
        setPackages(p || []);
      } else if (tab === "quotations") {
        const q = await eventsApi.getAllQuotations({
          status: quotationStatusFilter !== "all" ? quotationStatusFilter : undefined,
          search: quotationSearchQuery || undefined
        });
        setQuotationsList(q || []);
      } else if (tab === "reports") {
        const rep = await eventsApi.getEventReports({
          from_date: reportDateRange.from,
          to_date: reportDateRange.to
        });
        setReportsData(rep);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load event data");
    } finally {
      setLoading(false);
    }
  };

  // Open Full Event Workspace
  const handleOpenEventWorkspace = async (eventId) => {
    try {
      setSelectedEventId(eventId);
      const details = await eventsApi.getEventDetails(eventId);
      setEventDetails(details);
      setWorkspaceTab("overview");

      // Prepopulate BEO form if exists
      if (details.beo) {
        setBeoForm({
          theme_color: details.beo.theme_color || "",
          stage_setup: details.beo.stage_setup || "",
          seating_instructions: details.beo.seating_instructions || "",
          av_requirements: details.beo.av_requirements || "",
          food_service_style: details.beo.food_service_style || "Buffet",
          dietary_requirements: details.beo.dietary_requirements || "",
          bar_service_notes: details.beo.bar_service_notes || "",
          signage_instructions: details.beo.signage_instructions || "",
          special_instructions: details.beo.special_instructions || "",
          menus: (details.beo.menus && details.beo.menus.length > 0) ? details.beo.menus : beoForm.menus
        });
      }

      setModalType("event_workspace");
      setIsModalOpen(true);
    } catch (e) {
      toast.error("Failed to load event workspace");
    }
  };

  const reloadWorkspace = async () => {
    if (!selectedEventId) return;
    const details = await eventsApi.getEventDetails(selectedEventId);
    setEventDetails(details);
    fetchTabData();
  };

  // Status Colors
  const getStatusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    if (["confirmed", "active", "approved", "completed", "settled", "paid"].includes(s)) {
      return "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800";
    }
    if (["tentative", "quoted", "in progress", "partial", "partially_paid", "scheduled"].includes(s)) {
      return "bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400 border border-amber-200 dark:border-amber-800";
    }
    if (["inquiry", "draft"].includes(s)) {
      return "bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400 border border-blue-200 dark:border-blue-800";
    }
    if (["cancelled", "rejected", "expired", "unpaid"].includes(s)) {
      return "bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-200 dark:border-rose-800";
    }
    return "bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-gray-300 border border-gray-200 dark:border-zinc-700";
  };

  // Check Availability
  const handleCheckAvailability = async (e) => {
    e.preventDefault();
    try {
      const res = await eventsApi.checkVenueAvailability(
        availabilityForm.venue_id,
        availabilityForm.start_time,
        availabilityForm.end_time
      );
      setAvailabilityResult(res);
      if (res.available) {
        toast.success("Venue is available for booking!");
      } else {
        toast.error("Venue has conflicting bookings!");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to check availability");
    }
  };

  // Create Inquiry
  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    try {
      const id = await eventsApi.createEventInquiry(inquiryForm);
      toast.success("Event inquiry recorded successfully!");
      setIsModalOpen(false);
      fetchTabData();
      handleOpenEventWorkspace(id);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record inquiry");
    }
  };

  // Convert Inquiry to Booking
  const handleConvertBooking = async (eventId, confirmImmediately = false) => {
    try {
      await eventsApi.convertInquiryToBooking(eventId, {
        confirm_immediately: confirmImmediately,
        tentative_expiry_days: 3
      });
      toast.success(confirmImmediately ? "Booking Confirmed!" : "Marked as Tentative Booking!");
      reloadWorkspace();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to convert booking");
    }
  };

  // Save Quotation
  const handleQuotationSubmit = async (e) => {
    e.preventDefault();
    try {
      await eventsApi.createEventQuotation(selectedEventId, quotationForm);
      toast.success("Quotation version created successfully!");
      reloadWorkspace();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create quotation");
    }
  };

  // Approve Quotation
  const handleApproveQuotation = async (quotationId) => {
    try {
      await eventsApi.approveEventQuotation(selectedEventId, quotationId);
      toast.success("Quotation approved & agreed pricing locked!");
      reloadWorkspace();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to approve quotation");
    }
  };

  // Quotation Management Handlers
  const openCreateQuotationModal = (eventObj = null) => {
    if (eventObj) {
      setDirectQuotationForm({
        event_id: eventObj.id,
        customer_name: eventObj.customer_name || "",
        customer_phone: eventObj.customer_phone || "",
        customer_email: eventObj.customer_email || "",
        customer_company: eventObj.customer_company || "",
        customer_address: eventObj.customer_address || "",
        customer_pan: eventObj.customer_pan || "",
        event_title: eventObj.event_title || "",
        event_type: eventObj.event_type || "Wedding",
        primary_venue_id: eventObj.primary_venue_id || (venues[0]?.id || ""),
        package_id: eventObj.package_id || (packages[0]?.id || ""),
        start_date: eventObj.start_date ? new Date(eventObj.start_date).toISOString().slice(0, 16) : new Date(Date.now() + 86400000).toISOString().slice(0, 16),
        end_date: eventObj.end_date ? new Date(eventObj.end_date).toISOString().slice(0, 16) : new Date(Date.now() + 86400000 + 6 * 3600000).toISOString().slice(0, 16),
        guest_count: eventObj.guaranteed_guests || eventObj.expected_guests || 100,
        seating_layout: eventObj.seating_layout || "Round Table",
        discount_percent: 0,
        tax_percent: 13,
        advance_required_percent: 25,
        valid_until: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
        terms_conditions: "1. 25% non-refundable advance required to confirm venue booking.\n2. Final guest count must be confirmed 72 hours prior.\n3. Outside catering and alcohol subject to prior approval.\n4. Standard banquet halls and audio-visual guidelines apply.",
        items: [
          { item_type: "food_beverage", name: "Banquet Buffet Menu Package", quantity: eventObj.guaranteed_guests || 100, unit: "Pax", unit_price: 1200, tax_percent: 13, notes: "" },
          { item_type: "venue_rental", name: "Banquet Hall Rental Fee", quantity: 1, unit: "Day", unit_price: 15000, tax_percent: 13, notes: "Includes standard hall setup" }
        ]
      });
    } else {
      setDirectQuotationForm({
        event_id: "",
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        customer_company: "",
        customer_address: "",
        customer_pan: "",
        event_title: "",
        event_type: "Wedding",
        primary_venue_id: venues[0]?.id || "",
        package_id: packages[0]?.id || "",
        start_date: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
        end_date: new Date(Date.now() + 86400000 + 6 * 3600000).toISOString().slice(0, 16),
        guest_count: 100,
        seating_layout: "Round Table",
        discount_percent: 0,
        tax_percent: 13,
        advance_required_percent: 25,
        valid_until: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
        terms_conditions: "1. 25% non-refundable advance required to confirm venue booking.\n2. Final guest count must be confirmed 72 hours prior.\n3. Outside catering and alcohol subject to prior approval.\n4. Standard banquet halls and audio-visual guidelines apply.",
        items: [
          { item_type: "food_beverage", name: "Banquet Buffet Menu Package", quantity: 100, unit: "Pax", unit_price: 1200, tax_percent: 13, notes: "" },
          { item_type: "venue_rental", name: "Banquet Hall Rental Fee", quantity: 1, unit: "Day", unit_price: 15000, tax_percent: 13, notes: "Includes standard hall setup" }
        ]
      });
    }
    setModalType("create_quotation");
    setIsModalOpen(true);
  };

  const handleDirectQuotationSubmit = async (e, autoApprove = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!directQuotationForm.customer_name?.trim()) {
      toast.error("Customer name is required");
      return;
    }
    if (!directQuotationForm.customer_phone?.trim()) {
      toast.error("Customer phone is required");
      return;
    }
    if (!directQuotationForm.event_title?.trim()) {
      toast.error("Event title is required");
      return;
    }
    if (!directQuotationForm.items || directQuotationForm.items.length === 0) {
      toast.error("Please add at least one line item to the quotation");
      return;
    }

    try {
      setLoading(true);
      const res = await eventsApi.createDirectQuotation(directQuotationForm);
      toast.success(res.message || "Quotation generated successfully!");

      if (autoApprove && res.quotation?.id) {
        try {
          await eventsApi.approveEventQuotation(res.quotation.event_id, res.quotation.id);
          toast.success("Quotation approved and event pricing locked!");
        } catch (appErr) {
          console.warn("Quotation auto-approval notice:", appErr);
        }
      }

      setIsModalOpen(false);
      if (tab === "quotations") {
        fetchTabData();
      } else {
        setTab("quotations");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to create quotation");
    } finally {
      setLoading(false);
    }
  };

  const handleViewQuotation = async (quotationId) => {
    try {
      setLoading(true);
      const details = await eventsApi.getQuotationDetails(quotationId);
      setViewingQuotation(details);
      setModalType("view_quotation");
      setIsModalOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load quotation details");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveQuotationDirect = async (quotationId, eventId) => {
    try {
      await eventsApi.approveEventQuotation(eventId, quotationId);
      toast.success("Quotation approved! Agreed event pricing is now locked.");
      if (viewingQuotation && viewingQuotation.id === quotationId) {
        setViewingQuotation({ ...viewingQuotation, status: "Approved" });
      }
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to approve quotation");
    }
  };

  const handleAddPackageMenuItem = () => {
    const pkg = packages.find(p => String(p.id) === String(directQuotationForm.package_id)) || packages[0];
    const pax = Number(directQuotationForm.guest_count) || 100;
    const rate = pkg ? Number(pkg.base_price || 1200) : 1200;
    const name = pkg ? `${pkg.name} (Banquet Menu)` : "Banquet Buffet Menu Package";
    setDirectQuotationForm(prev => ({
      ...prev,
      items: [
        ...prev.items,
        { item_type: "food_beverage", name, quantity: pax, unit: "Pax", unit_price: rate, tax_percent: 13, notes: "" }
      ]
    }));
  };

  const handleAddVenueRentalItem = () => {
    const ven = venues.find(v => String(v.id) === String(directQuotationForm.primary_venue_id)) || venues[0];
    const rate = ven ? Number(ven.rental_rate_per_day || 15000) : 15000;
    const name = ven ? `${ven.name} Hall Rental` : "Banquet Hall Rental Fee";
    setDirectQuotationForm(prev => ({
      ...prev,
      items: [
        ...prev.items,
        { item_type: "venue_rental", name, quantity: 1, unit: "Day", unit_price: rate, tax_percent: 13, notes: "Includes standard setup" }
      ]
    }));
  };

  const handleAddCustomItem = () => {
    setDirectQuotationForm(prev => ({
      ...prev,
      items: [
        ...prev.items,
        { item_type: "other", name: "", quantity: 1, unit: "Nos", unit_price: 0, tax_percent: 13, notes: "" }
      ]
    }));
  };

  const handleRemoveItem = (index) => {
    setDirectQuotationForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleUpdateItem = (index, field, value) => {
    setDirectQuotationForm(prev => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  };

  // Save BEO
  const handleBeoSubmit = async (e) => {
    e.preventDefault();
    try {
      await eventsApi.saveEventBeo(selectedEventId, beoForm);
      toast.success("Banquet Event Order (BEO) saved & department tasks synced!");
      reloadWorkspace();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save BEO");
    }
  };

  // Record Payment
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await eventsApi.recordEventPayment(selectedEventId, paymentForm);
      toast.success("Payment recorded successfully!");
      setPaymentForm({ amount: 1000, payment_type: "advance_deposit", payment_method: "cash", payment_date: new Date().toISOString().slice(0, 16), reference_no: "", notes: "" });
      reloadWorkspace();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to record payment");
    }
  };

  // Final Settlement Invoice
  const handleGenerateInvoice = async () => {
    try {
      const res = await eventsApi.generateEventFinalInvoice(selectedEventId, {});
      toast.success(`Official invoice ${res.invoice_number} generated in Invoices & Accounts!`);
      reloadWorkspace();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to generate invoice");
    }
  };

  // Task Status Toggle
  const handleTaskToggle = async (taskId, currentStatus) => {
    try {
      const newStatus = currentStatus === "Completed" ? "Pending" : "Completed";
      await eventsApi.updateEventDepartmentTaskStatus(selectedEventId, taskId, newStatus);
      reloadWorkspace();
    } catch (err) {
      toast.error("Failed to update task status");
    }
  };

  // Checklist Toggle
  const handleChecklistToggle = async (itemId, currentCompleted) => {
    try {
      await eventsApi.toggleChecklistItem(selectedEventId, itemId, !currentCompleted);
      reloadWorkspace();
    } catch (err) {
      toast.error("Failed to update checklist item");
    }
  };

  // Cancellation Submit
  const handleCancelSubmit = async (e) => {
    e.preventDefault();
    try {
      await eventsApi.cancelEvent(selectedEventId, cancelForm);
      toast.success("Event cancelled and venue reservations released");
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to cancel event");
    }
  };

  // Add Venue Submit
  const handleVenueSubmit = async (e) => {
    e.preventDefault();
    try {
      await eventsApi.createEventVenue(venueForm);
      toast.success("Venue created successfully");
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save venue");
    }
  };

  // Add Package Submit
  const handlePackageSubmit = async (e) => {
    e.preventDefault();
    try {
      await eventsApi.createEventPackage(packageForm);
      toast.success("Event package created successfully");
      setIsModalOpen(false);
      fetchTabData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save package");
    }
  };

  // Export Reports CSV
  const handleExportCSV = () => {
    if (!reportsData) return;
    let csv = "Venue,Events Hosted,Guests Served,Revenue Generated\n";
    (reportsData.venue_utilization || []).forEach(v => {
      csv += `"${v.name}",${v.events_hosted || 0},${v.total_guests_served || 0},${v.total_revenue_generated || 0}\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `event_utilization_report_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return (
    <div className="p-4 md:p-6 min-h-screen bg-restro-green-light dark:bg-restro-gray text-restro-text dark:text-white">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Event & Banquet Management
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-restro-green/10 text-restro-green dark:bg-restro-green/20">
              eHospitality
            </span>
          </div>
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">
            Complete lifecycle management: Inquiries, Venue Calendars, Quotations, BEOs, Live Checklists, Billing & Settlements.
          </p>
        </div>

        {/* Global Action Buttons */}
        <div className="flex gap-2 flex-wrap items-center">
          <button
            onClick={() => {
              setInquiryForm({
                customer_name: "", customer_phone: "", customer_email: "", customer_company: "",
                customer_address: "", customer_pan: "", event_title: "", event_type: "Wedding",
                primary_venue_id: venues[0]?.id || "", package_id: packages[0]?.id || "",
                start_date: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
                end_date: new Date(Date.now() + 86400000 + 6 * 3600000).toISOString().slice(0, 16),
                expected_guests: 100, guaranteed_guests: 100, seating_layout: "Round Table",
                estimated_budget: 0, sales_owner: "", follow_up_date: "", follow_up_notes: "", special_requirements: ""
              });
              setModalType("new_inquiry");
              setIsModalOpen(true);
            }}
            className="px-4 py-2.5 rounded-2xl bg-restro-green hover:bg-restro-green-button-hover text-white font-bold flex items-center gap-2 shadow-sm transition text-xs md:text-sm"
          >
            <IconPlus size={18} /> New Event Inquiry
          </button>

          <button
            onClick={() => {
              setAvailabilityForm({
                venue_id: venues[0]?.id || "",
                start_time: new Date().toISOString().slice(0, 16),
                end_time: new Date(Date.now() + 6 * 3600000).toISOString().slice(0, 16)
              });
              setAvailabilityResult(null);
              setModalType("check_availability");
              setIsModalOpen(true);
            }}
            className="px-3.5 py-2.5 rounded-2xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-gray-200 font-bold flex items-center gap-1.5 shadow-sm hover:bg-gray-50 dark:hover:bg-zinc-700 transition text-xs md:text-sm"
          >
            <IconClock size={18} className="text-restro-green" /> Check Venue Availability
          </button>

          <button
            onClick={() => openCreateQuotationModal()}
            className="px-3.5 py-2.5 rounded-2xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-gray-200 font-bold flex items-center gap-1.5 shadow-sm hover:bg-gray-50 dark:hover:bg-zinc-700 transition text-xs md:text-sm"
          >
            <IconFileText size={18} className="text-restro-green" /> + Create Quotation
          </button>

          <button
            onClick={fetchTabData}
            className="p-2.5 rounded-2xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-gray-600 dark:text-gray-300 hover:text-restro-green transition"
            title="Refresh"
          >
            <IconRefresh size={18} />
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-gray-200 dark:border-zinc-800 gap-1 md:gap-3 mb-6 overflow-x-auto no-scrollbar">
        {[
          { id: "dashboard", label: "Dashboard", icon: <IconTrendingUp size={17} /> },
          { id: "events", label: "Inquiries & Bookings", icon: <IconCalendarEvent size={17} /> },
          { id: "quotations", label: "Quotations", icon: <IconFileText size={17} /> },
          { id: "calendar", label: "Venue Calendar", icon: <IconClock size={17} /> },
          { id: "venues", label: "Venues & Halls", icon: <IconBuildingWarehouse size={17} /> },
          { id: "packages", label: "Banquet Packages", icon: <IconToolsKitchen2 size={17} /> },
          { id: "reports", label: "Reports & Analytics", icon: <IconFileText size={17} /> }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`py-3 px-3.5 text-xs md:text-sm font-semibold border-b-2 -mb-px flex items-center gap-2 whitespace-nowrap transition rounded-t-lg ${
              tab === t.id
                ? "border-restro-green text-restro-green bg-restro-green/5 dark:bg-restro-green/10"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex flex-col justify-center items-center py-28">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-restro-green"></div>
          <p className="text-xs text-gray-400 mt-3 font-medium">Syncing event schedules & venue bookings...</p>
        </div>
      ) : (
        <div>
          {/* TAB 1: DASHBOARD */}
          {tab === "dashboard" && dashboardStats && (
            <div className="space-y-6">
              {/* Metric Cards Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Total Bookings</span>
                  <div className="text-2xl font-black text-gray-900 dark:text-white mt-1">
                    {(dashboardStats.status_breakdown || []).reduce((acc, r) => acc + Number(r.count || 0), 0)}
                  </div>
                  <span className="text-[10px] text-gray-400">All lifecycle stages</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Confirmed Events</span>
                  <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                    {dashboardStats.status_breakdown?.find(s => s.status === 'Confirmed')?.count || 0}
                  </div>
                  <span className="text-[10px] text-emerald-500">Advance deposits locked</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-amber-100 dark:border-amber-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">Tentative Holds</span>
                  <div className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
                    {dashboardStats.status_breakdown?.find(s => s.status === 'Tentative')?.count || 0}
                  </div>
                  <span className="text-[10px] text-amber-500">Awaiting advance payment</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-blue-100 dark:border-blue-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">Active Inquiries</span>
                  <div className="text-2xl font-black text-blue-600 dark:text-blue-400 mt-1">
                    {dashboardStats.status_breakdown?.find(s => s.status === 'Inquiry' || s.status === 'Quoted')?.count || 0}
                  </div>
                  <span className="text-[10px] text-blue-500">Sales pipeline prospects</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-purple-100 dark:border-purple-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">Collected Advances</span>
                  <div className="text-xl font-black text-purple-600 dark:text-purple-400 mt-1">
                    {currency} {Number(dashboardStats.financials?.total_collected || 0).toFixed(0)}
                  </div>
                  <span className="text-[10px] text-purple-500">Paid into accounts</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-rose-100 dark:border-rose-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">Outstanding Due</span>
                  <div className="text-xl font-black text-rose-600 dark:text-rose-400 mt-1">
                    {currency} {Number(dashboardStats.financials?.total_outstanding_due || 0).toFixed(0)}
                  </div>
                  <span className="text-[10px] text-rose-500">Receivables balance</span>
                </div>
              </div>

              {/* Upcoming Events Next 7 Days */}
              <div className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white text-sm">Upcoming Functions & Events (Next 7 Days)</h3>
                    <p className="text-xs text-gray-400">Scheduled banquets, conferences & receptions requiring operational setup</p>
                  </div>
                  <button onClick={() => setTab("events")} className="text-xs font-bold text-restro-green hover:underline">
                    View All Events →
                  </button>
                </div>

                <div className="space-y-3">
                  {(dashboardStats.upcoming_events || []).map(ev => (
                    <div
                      key={ev.id}
                      onClick={() => handleOpenEventWorkspace(ev.id)}
                      className="p-4 rounded-2xl bg-gray-50 dark:bg-zinc-800/50 border border-gray-100 dark:border-zinc-800/80 hover:border-restro-green/50 cursor-pointer transition flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3"
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-gray-500">{ev.event_number}</span>
                          <span className="font-black text-sm text-gray-900 dark:text-white">{ev.event_title}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusBadge(ev.status)}`}>
                            {ev.status}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                          <span>Venue: <strong className="text-gray-700 dark:text-gray-300">{ev.venue_name || "TBD"}</strong></span>
                          <span>•</span>
                          <span>Guests: <strong className="text-gray-700 dark:text-gray-300">{ev.guaranteed_guests} pax</strong></span>
                          <span>•</span>
                          <span>Date: <strong className="text-gray-700 dark:text-gray-300">{new Date(ev.start_date).toLocaleString()}</strong></span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="px-3 py-1.5 rounded-xl bg-restro-green/10 text-restro-green hover:bg-restro-green hover:text-white text-xs font-bold transition">
                          Open Workspace
                        </button>
                      </div>
                    </div>
                  ))}
                  {(!dashboardStats.upcoming_events || dashboardStats.upcoming_events.length === 0) && (
                    <div className="text-center py-8 text-gray-400 text-xs">
                      No functions scheduled in the next 7 days.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: INQUIRIES & BOOKINGS LIST */}
          {tab === "events" && (
            <div className="space-y-4">
              {/* Search & Filters */}
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                <div className="relative flex-1">
                  <IconSearch size={16} className="absolute left-3.5 top-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by title, event #, customer name, phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs md:text-sm focus:outline-none focus:border-restro-green"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="py-2.5 px-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs focus:outline-none"
                  >
                    <option value="all">All Statuses</option>
                    <option value="Inquiry">Inquiry</option>
                    <option value="Quoted">Quoted</option>
                    <option value="Tentative">Tentative</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Settled">Settled</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>

                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="py-2.5 px-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs focus:outline-none"
                  >
                    <option value="all">All Types</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Reception">Reception</option>
                    <option value="Conference">Conference</option>
                    <option value="Seminar">Seminar</option>
                    <option value="Birthday">Birthday</option>
                    <option value="Corporate Function">Corporate</option>
                    <option value="Other">Other</option>
                  </select>

                  <button
                    onClick={fetchTabData}
                    className="px-3.5 py-2.5 rounded-xl bg-restro-green text-white text-xs font-bold hover:bg-restro-green-button-hover transition"
                  >
                    Filter
                  </button>
                </div>
              </div>

              {/* Events Table */}
              <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs md:text-sm">
                    <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                      <tr>
                        <th className="p-3.5">Event # & Title</th>
                        <th className="p-3.5">Customer Contact</th>
                        <th className="p-3.5">Venue & Date</th>
                        <th className="p-3.5 text-center">Pax (Guaranteed)</th>
                        <th className="p-3.5 text-right">Agreed Total</th>
                        <th className="p-3.5 text-right text-emerald-600">Paid</th>
                        <th className="p-3.5 text-right text-rose-600">Balance Due</th>
                        <th className="p-3.5 text-center">Status</th>
                        <th className="p-3.5 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                      {eventsList.map(ev => (
                        <tr key={ev.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                          <td className="p-3.5">
                            <div className="font-mono text-xs font-bold text-gray-400">{ev.event_number}</div>
                            <div className="font-bold text-gray-900 dark:text-white">{ev.event_title}</div>
                            <span className="text-[10px] text-gray-400 capitalize">{ev.event_type}</span>
                          </td>
                          <td className="p-3.5">
                            <div className="font-semibold text-gray-900 dark:text-white">{ev.customer_name}</div>
                            <div className="text-[11px] text-gray-400">{ev.customer_phone}</div>
                          </td>
                          <td className="p-3.5">
                            <div className="font-bold text-purple-600">{ev.venue_name || "TBD"}</div>
                            <div className="text-[11px] text-gray-500">
                              {new Date(ev.start_date).toLocaleDateString()} {new Date(ev.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </td>
                          <td className="p-3.5 text-center font-bold">
                            {ev.guaranteed_guests || ev.expected_guests || 0} pax
                          </td>
                          <td className="p-3.5 text-right font-black">
                            {currency} {Number(ev.net_total_amount || ev.agreed_total_amount || 0).toFixed(2)}
                          </td>
                          <td className="p-3.5 text-right font-bold text-emerald-600">
                            {currency} {Number(ev.total_paid_amount || 0).toFixed(2)}
                          </td>
                          <td className="p-3.5 text-right font-bold text-rose-600">
                            {currency} {Number(ev.balance_due_amount || 0).toFixed(2)}
                          </td>
                          <td className="p-3.5 text-center">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${getStatusBadge(ev.status)}`}>
                              {ev.status}
                            </span>
                          </td>
                          <td className="p-3.5 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => openCreateQuotationModal(ev)}
                                className="px-2.5 py-1.5 rounded-xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-gray-200 text-xs font-bold hover:bg-gray-50 dark:hover:bg-zinc-700 transition shadow-xs flex items-center gap-1"
                                title="Create Banquet Quotation for this event"
                              >
                                <IconFileText size={14} className="text-restro-green" /> Quote
                              </button>
                              <button
                                onClick={() => handleOpenEventWorkspace(ev.id)}
                                className="px-3 py-1.5 rounded-xl bg-restro-green text-white text-xs font-bold hover:bg-restro-green-button-hover transition shadow-sm"
                              >
                                Manage Workspace
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {eventsList.length === 0 && (
                        <tr>
                          <td colSpan={9} className="p-8 text-center text-gray-400 text-xs">
                            No events or inquiries found. Click "New Event Inquiry" to start.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB: QUOTATIONS & PROPOSALS */}
          {tab === "quotations" && (
            <div className="space-y-4">
              {/* Search & Filters Bar */}
              <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                <div className="relative flex-1">
                  <IconSearch size={16} className="absolute left-3.5 top-3.5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by quote #, client name, phone, or event title..."
                    value={quotationSearchQuery}
                    onChange={(e) => setQuotationSearchQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") fetchTabData(); }}
                    className="w-full pl-9 pr-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs md:text-sm focus:outline-none focus:border-restro-green"
                  />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={quotationStatusFilter}
                    onChange={(e) => setQuotationStatusFilter(e.target.value)}
                    className="py-2.5 px-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs focus:outline-none"
                  >
                    <option value="all">All Statuses</option>
                    <option value="Draft">Draft</option>
                    <option value="Sent">Sent</option>
                    <option value="Approved">Approved</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>

                  <button
                    onClick={fetchTabData}
                    className="px-3.5 py-2.5 rounded-xl bg-restro-green text-white text-xs font-bold hover:bg-restro-green-button-hover transition"
                  >
                    Filter
                  </button>

                  <button
                    onClick={() => openCreateQuotationModal()}
                    className="px-3.5 py-2.5 rounded-xl bg-restro-green text-white text-xs font-bold hover:bg-restro-green-button-hover transition flex items-center gap-1.5 shadow-sm"
                  >
                    <IconPlus size={16} /> New Quotation
                  </button>
                </div>
              </div>

              {/* Metric Highlights */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Total Quotations</span>
                  <div className="text-2xl font-black text-gray-900 dark:text-white mt-1">
                    {quotationsList.length}
                  </div>
                  <span className="text-[10px] text-gray-400">All proposals created</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Approved & Locked</span>
                  <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                    {quotationsList.filter(q => q.status === "Approved").length}
                  </div>
                  <span className="text-[10px] text-emerald-500">Agreed commercial value</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-amber-100 dark:border-amber-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400">Drafts & Proposals</span>
                  <div className="text-2xl font-black text-amber-600 dark:text-amber-400 mt-1">
                    {quotationsList.filter(q => q.status !== "Approved" && q.status !== "Cancelled").length}
                  </div>
                  <span className="text-[10px] text-amber-500">Awaiting client decision</span>
                </div>

                <div className="bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-purple-100 dark:border-purple-950/50 shadow-sm">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400">Total Quoted Value</span>
                  <div className="text-xl font-black text-purple-600 dark:text-purple-400 mt-1">
                    {currency} {quotationsList.reduce((acc, q) => acc + (Number(q.grand_total) || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <span className="text-[10px] text-purple-500">Across active pipeline</span>
                </div>
              </div>

              {/* Quotations Table */}
              <div className="bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 rounded-3xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs md:text-sm">
                    <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                      <tr>
                        <th className="p-3.5">Quotation #</th>
                        <th className="p-3.5">Client & Contact</th>
                        <th className="p-3.5">Event & Type</th>
                        <th className="p-3.5">Venue & Date</th>
                        <th className="p-3.5 text-center">Pax</th>
                        <th className="p-3.5 text-right">Grand Total</th>
                        <th className="p-3.5 text-right text-purple-600">Advance Req.</th>
                        <th className="p-3.5 text-center">Status</th>
                        <th className="p-3.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                      {filteredQuotations.map(q => (
                        <tr key={q.id} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30 transition">
                          <td className="p-3.5">
                            <div className="font-mono text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                              <IconFileText size={15} className="text-restro-green" />
                              {q.quotation_number}
                            </div>
                            <div className="text-[10px] text-gray-400">Ver. {q.version} • {new Date(q.created_at).toLocaleDateString()}</div>
                          </td>
                          <td className="p-3.5">
                            <div className="font-semibold text-gray-900 dark:text-white">{q.customer_name}</div>
                            <div className="text-[11px] text-gray-400">{q.customer_phone} {q.customer_company ? `• ${q.customer_company}` : ""}</div>
                          </td>
                          <td className="p-3.5">
                            <div className="font-bold text-gray-800 dark:text-gray-200">{q.event_title}</div>
                            <span className="text-[10px] text-gray-400 capitalize">{q.event_type}</span>
                          </td>
                          <td className="p-3.5">
                            <div className="font-bold text-purple-600">{q.venue_name || "TBD"}</div>
                            <div className="text-[11px] text-gray-500">
                              {q.start_date ? new Date(q.start_date).toLocaleDateString() : "Flexible Date"}
                            </div>
                          </td>
                          <td className="p-3.5 text-center font-bold">
                            {q.guest_count} pax
                          </td>
                          <td className="p-3.5 text-right font-black text-gray-900 dark:text-white">
                            {currency} {Number(q.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="p-3.5 text-right font-bold text-purple-600">
                            {currency} {Number(q.advance_required_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <div className="text-[10px] text-gray-400">({q.advance_required_percent || 0}%)</div>
                          </td>
                          <td className="p-3.5 text-center">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${getStatusBadge(q.status)}`}>
                              {q.status}
                            </span>
                          </td>
                          <td className="p-3.5 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => handleViewQuotation(q.id)}
                                className="px-2.5 py-1.5 rounded-xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-gray-200 text-xs font-bold hover:bg-gray-50 dark:hover:bg-zinc-700 transition shadow-xs flex items-center gap-1"
                                title="View and Print Banquet Proposal"
                              >
                                <IconPrinter size={14} className="text-restro-green" /> Proposal
                              </button>
                              {q.status !== "Approved" && (
                                <button
                                  onClick={() => handleApproveQuotationDirect(q.id, q.event_id)}
                                  className="px-2.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition shadow-xs flex items-center gap-1"
                                  title="Approve Proposal & Lock Agreed Pricing"
                                >
                                  <IconCheck size={14} /> Approve
                                </button>
                              )}
                              <button
                                onClick={() => handleOpenEventWorkspace(q.event_id)}
                                className="px-2.5 py-1.5 rounded-xl bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 text-xs font-bold transition shadow-xs"
                                title="Open Full Event Workspace"
                              >
                                Workspace
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredQuotations.length === 0 && (
                        <tr>
                          <td colSpan={9} className="p-8 text-center text-gray-400 text-xs">
                            No quotations found matching your criteria. Click "+ New Quotation" to create one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: VENUE CALENDAR */}
          {tab === "calendar" && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-bold text-sm text-gray-900 dark:text-white">Active Venue & Banquet Schedule</h3>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Confirmed
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span> Tentative
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {(calendarFeed.events || []).map(ev => (
                    <div
                      key={ev.id}
                      onClick={() => handleOpenEventWorkspace(ev.id)}
                      className="p-4 rounded-2xl bg-gray-50 dark:bg-zinc-800/60 border border-gray-200/50 dark:border-zinc-700/50 hover:border-restro-green cursor-pointer transition space-y-2"
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-xs text-purple-600">{ev.venue_name || "Venue"}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusBadge(ev.status)}`}>
                          {ev.status}
                        </span>
                      </div>
                      <div className="font-black text-sm text-gray-900 dark:text-white">{ev.title}</div>
                      <div className="text-[11px] text-gray-400">
                        {new Date(ev.start).toLocaleString()} - {new Date(ev.end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))}
                  {(!calendarFeed.events || calendarFeed.events.length === 0) && (
                    <div className="col-span-3 text-center py-10 text-gray-400 text-xs">
                      No calendar events scheduled in this period.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: VENUES & HALLS */}
          {tab === "venues" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-gray-900 dark:text-white text-base">Venues, Halls & Lawns</h3>
                <button
                  onClick={() => {
                    setVenueForm({
                      name: "", code: "", venue_type: "Banquet Hall", capacity_min: 20, capacity_max: 300,
                      rental_rate_per_day: 5000, rental_rate_per_hour: 500, setup_buffer_mins: 60,
                      teardown_buffer_mins: 60, is_exclusive: true, description: "", amenities: "AC, Sound System, Stage"
                    });
                    setModalType("add_venue");
                    setIsModalOpen(true);
                  }}
                  className="px-3 py-2 rounded-xl bg-restro-green text-white text-xs font-bold flex items-center gap-1.5"
                >
                  <IconPlus size={16} /> Add Venue
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {venues.map(v => (
                  <div key={v.id} className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-black text-base text-gray-900 dark:text-white">{v.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getStatusBadge(v.status)}`}>
                        {v.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Type: <strong>{v.venue_type}</strong> • Capacity: <strong>{v.capacity_min} - {v.capacity_max} guests</strong>
                    </div>
                    <div className="p-3 rounded-2xl bg-gray-50 dark:bg-zinc-800/60 text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Daily Rental:</span>
                        <span className="font-bold">{currency} {Number(v.rental_rate_per_day || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Hourly Rental:</span>
                        <span className="font-bold">{currency} {Number(v.rental_rate_per_hour || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Buffer Window:</span>
                        <span className="font-medium">{v.setup_buffer_mins}m setup / {v.teardown_buffer_mins}m teardown</span>
                      </div>
                    </div>
                    <div className="text-[11px] text-gray-400 truncate">
                      Amenities: {Array.isArray(v.amenities) ? v.amenities.join(', ') : (v.amenities || "Standard Amenities")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 5: BANQUET PACKAGES */}
          {tab === "packages" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-bold text-gray-900 dark:text-white text-base">Standard Banquet Packages</h3>
                <button
                  onClick={() => {
                    setPackageForm({
                      name: "", package_type: "per_person", base_price: 1200, min_guests: 50,
                      description: "", inclusions: ["Welcome Mocktail", "Buffet Menu", "Dessert"]
                    });
                    setModalType("add_package");
                    setIsModalOpen(true);
                  }}
                  className="px-3 py-2 rounded-xl bg-restro-green text-white text-xs font-bold flex items-center gap-1.5"
                >
                  <IconPlus size={16} /> Create Package
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {packages.map(p => (
                  <div key={p.id} className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-black text-base text-gray-900 dark:text-white">{p.name}</span>
                      <span className="font-black text-emerald-600 text-sm">
                        {currency} {Number(p.base_price).toFixed(2)} {p.package_type === 'per_person' ? '/ pax' : ''}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{p.description || "Comprehensive banquet food & service package"}</p>
                    <div className="p-3 rounded-2xl bg-gray-50 dark:bg-zinc-800/60 text-xs">
                      <span className="font-bold text-[10px] uppercase text-gray-400 block mb-1">Inclusions:</span>
                      <ul className="list-disc list-inside space-y-0.5 text-gray-600 dark:text-gray-300">
                        {(p.inclusions || []).map((inc, i) => (
                          <li key={i}>{inc}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 6: REPORTS & ANALYTICS */}
          {tab === "reports" && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-white dark:bg-zinc-900 p-4 rounded-2xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-400">From:</span>
                    <input
                      type="date"
                      value={reportDateRange.from}
                      onChange={(e) => setReportDateRange({ ...reportDateRange, from: e.target.value })}
                      className="p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="text-gray-400">To:</span>
                    <input
                      type="date"
                      value={reportDateRange.to}
                      onChange={(e) => setReportDateRange({ ...reportDateRange, to: e.target.value })}
                      className="p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                    />
                  </div>
                  <button onClick={fetchTabData} className="px-3 py-2 rounded-xl bg-restro-green text-white text-xs font-bold">
                    Generate Report
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button onClick={handleExportCSV} className="px-3 py-2 rounded-xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-xs font-bold flex items-center gap-1.5">
                    <IconDownload size={15} /> Export CSV
                  </button>
                  <button onClick={() => window.print()} className="px-3 py-2 rounded-xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-xs font-bold flex items-center gap-1.5">
                    <IconPrinter size={15} /> Print
                  </button>
                </div>
              </div>

              {reportsData && (
                <div className="space-y-6">
                  {/* Venue Utilization Table */}
                  <div className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-3">Venue Utilization & Revenue Contribution</h3>
                    <table className="w-full text-left text-xs md:text-sm">
                      <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                        <tr>
                          <th className="p-3">Venue Name</th>
                          <th className="p-3 text-center">Events Hosted</th>
                          <th className="p-3 text-center">Total Guests Served</th>
                          <th className="p-3 text-right">Total Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                        {(reportsData.venue_utilization || []).map((vu, idx) => (
                          <tr key={idx}>
                            <td className="p-3 font-bold text-gray-900 dark:text-white">{vu.name}</td>
                            <td className="p-3 text-center">{vu.events_hosted || 0}</td>
                            <td className="p-3 text-center">{vu.total_guests_served || 0} pax</td>
                            <td className="p-3 text-right font-black text-emerald-600">
                              {currency} {Number(vu.total_revenue_generated || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Outstanding Receivables Table */}
                  <div className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-gray-100 dark:border-zinc-800 shadow-sm">
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white mb-3">Outstanding Event Receivables Aging</h3>
                    <table className="w-full text-left text-xs md:text-sm">
                      <thead className="bg-gray-50 dark:bg-zinc-800/60 text-gray-500 uppercase text-[11px] font-bold border-b border-gray-100 dark:border-zinc-800">
                        <tr>
                          <th className="p-3">Event # & Title</th>
                          <th className="p-3">Customer</th>
                          <th className="p-3 text-right">Net Bill</th>
                          <th className="p-3 text-right text-emerald-600">Paid</th>
                          <th className="p-3 text-right text-rose-600">Balance Due</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-zinc-800/50">
                        {(reportsData.receivables || []).map((rc, idx) => (
                          <tr key={idx}>
                            <td className="p-3 font-bold">{rc.event_number} - {rc.event_title}</td>
                            <td className="p-3">{rc.customer_name} ({rc.customer_phone})</td>
                            <td className="p-3 text-right font-semibold">{currency} {Number(rc.net_total_amount || 0).toFixed(2)}</td>
                            <td className="p-3 text-right font-bold text-emerald-600">{currency} {Number(rc.total_paid_amount || 0).toFixed(2)}</td>
                            <td className="p-3 text-right font-black text-rose-600">{currency} {Number(rc.balance_due_amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ================= MODALS SECTION ================= */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs overflow-y-auto">
          <div className={`bg-white dark:bg-zinc-950 border border-gray-100 dark:border-zinc-800 rounded-3xl w-full shadow-2xl overflow-hidden my-8 ${modalType === "view_quotation" || modalType === "create_quotation" ? "max-w-5xl" : "max-w-4xl"}`}>
            <div className="flex justify-between items-center p-5 border-b border-gray-100 dark:border-zinc-800 bg-gray-50/50 dark:bg-zinc-900/50 no-print">
              <h3 className="text-lg font-bold dark:text-white capitalize flex items-center gap-2">
                {modalType === "new_inquiry" && <><IconCalendarEvent className="text-restro-green" size={20} /> New Event Inquiry</>}
                {modalType === "check_availability" && <><IconClock className="text-restro-green" size={20} /> Check Venue Availability</>}
                {modalType === "add_venue" && <><IconBuildingWarehouse className="text-restro-green" size={20} /> Register Venue / Hall</>}
                {modalType === "add_package" && <><IconToolsKitchen2 className="text-restro-green" size={20} /> Create Banquet Package</>}
                {modalType === "event_workspace" && <><IconCalendarEvent className="text-restro-green" size={20} /> {eventDetails?.event_title} ({eventDetails?.event_number})</>}
                {modalType === "create_quotation" && <><IconFileText className="text-restro-green" size={20} /> Banquet Quotation Proposal</>}
                {modalType === "view_quotation" && <><IconPrinter className="text-restro-green" size={20} /> Banquet Quotation & Proposal</>}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="p-1 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <IconX size={20} />
              </button>
            </div>

            <div className="p-6 max-h-[80vh] overflow-y-auto">
              {/* MODAL: EVENT WORKSPACE */}
              {modalType === "event_workspace" && eventDetails && (
                <div className="space-y-6">
                  {/* Workspace Tabs */}
                  <div className="flex border-b border-gray-200 dark:border-zinc-800 gap-2 overflow-x-auto no-scrollbar pb-1">
                    {[
                      { id: "overview", label: "Overview & Booking", icon: <IconInfoCircle size={15} /> },
                      { id: "quotations", label: "Quotations & Pricing", icon: <IconFileText size={15} /> },
                      { id: "beo", label: "Banquet Event Order (BEO)", icon: <IconToolsKitchen2 size={15} /> },
                      { id: "tasks", label: "Department Tasks", icon: <IconUsers size={15} /> },
                      { id: "checklist", label: "Live Checklist", icon: <IconListCheck size={15} /> },
                      { id: "payments", label: "Billing & Deposits", icon: <IconCash size={15} /> },
                      { id: "amendments", label: "Audit Trail", icon: <IconFileInvoice size={15} /> }
                    ].map(st => (
                      <button
                        key={st.id}
                        onClick={() => setWorkspaceTab(st.id)}
                        className={`py-2 px-3 rounded-xl text-xs font-bold flex items-center gap-1.5 transition ${
                          workspaceTab === st.id
                            ? "bg-restro-green text-white"
                            : "bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                        }`}
                      >
                        {st.icon} {st.label}
                      </button>
                    ))}
                  </div>

                  {/* SUBTAB 1: OVERVIEW */}
                  {workspaceTab === "overview" && (
                    <div className="space-y-4 text-xs md:text-sm">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800">
                        <div>
                          <span className="text-[10px] uppercase font-bold text-gray-400">Status</span>
                          <div className="mt-1">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${getStatusBadge(eventDetails.status)}`}>
                              {eventDetails.status}
                            </span>
                          </div>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold text-gray-400">Guaranteed Guests</span>
                          <div className="font-bold text-gray-900 dark:text-white mt-1">{eventDetails.guaranteed_guests} pax</div>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold text-gray-400">Primary Venue</span>
                          <div className="font-bold text-purple-600 mt-1">{eventDetails.venue_name || "TBD"}</div>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold text-gray-400">Total Bill Value</span>
                          <div className="font-black text-gray-900 dark:text-white mt-1">
                            {currency} {Number(eventDetails.net_total_amount || eventDetails.agreed_total_amount || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>

                      {/* Customer Info */}
                      <div className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 space-y-2">
                        <h4 className="font-bold text-xs uppercase text-gray-400">Customer & Sales Coordinator</h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>Customer: <strong>{eventDetails.customer_name}</strong></div>
                          <div>Phone: <strong>{eventDetails.customer_phone}</strong></div>
                          <div>Email: <strong>{eventDetails.customer_email || "N/A"}</strong></div>
                          <div>Company: <strong>{eventDetails.customer_company || "Individual"}</strong></div>
                          <div>Sales Owner: <strong>{eventDetails.sales_owner || "Banquet Sales"}</strong></div>
                          <div>Follow-up: <strong>{eventDetails.follow_up_date || "No follow-up set"}</strong></div>
                        </div>
                      </div>

                      {/* Conversion Actions */}
                      {eventDetails.status === "Inquiry" && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleConvertBooking(eventDetails.id, false)}
                            className="px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs"
                          >
                            Mark Tentative Hold (3-Day Expiry)
                          </button>
                          <button
                            onClick={() => handleConvertBooking(eventDetails.id, true)}
                            className="px-4 py-2.5 rounded-xl bg-restro-green hover:bg-restro-green-button-hover text-white font-bold text-xs"
                          >
                            Confirm Booking Immediately
                          </button>
                        </div>
                      )}

                      {eventDetails.status === "Tentative" && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleConvertBooking(eventDetails.id, true)}
                            className="px-4 py-2.5 rounded-xl bg-restro-green hover:bg-restro-green-button-hover text-white font-bold text-xs"
                          >
                            Confirm Booking (Advance Received)
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* SUBTAB 2: QUOTATIONS */}
                  {workspaceTab === "quotations" && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center">
                        <h4 className="font-bold text-xs uppercase text-gray-400">Quotation Versions</h4>
                        <button
                          onClick={() => {
                            setQuotationForm({
                              ...quotationForm,
                              guest_count: eventDetails.guaranteed_guests || 100
                            });
                            // Trigger inline new quotation
                          }}
                          className="px-3 py-1.5 rounded-xl bg-restro-green text-white text-xs font-bold"
                        >
                          + New Quotation Version
                        </button>
                      </div>

                      {/* Existing Quotations List */}
                      <div className="space-y-3">
                        {(eventDetails.quotations || []).map(q => (
                          <div key={q.id} className="p-4 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 space-y-2 text-xs">
                            <div className="flex justify-between items-center">
                              <span className="font-bold text-sm text-gray-900 dark:text-white">
                                {q.quotation_number} • Version {q.version}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full font-bold ${getStatusBadge(q.status)}`}>
                                {q.status}
                              </span>
                            </div>
                            <div className="grid grid-cols-4 gap-2 text-gray-500">
                              <div>Guests: <strong>{q.guest_count}</strong></div>
                              <div>F&B Total: <strong>{currency} {Number(q.food_beverage_amount).toFixed(2)}</strong></div>
                              <div>Venue Rental: <strong>{currency} {Number(q.venue_rental_amount).toFixed(2)}</strong></div>
                              <div>Grand Total: <strong className="text-emerald-600">{currency} {Number(q.grand_total).toFixed(2)}</strong></div>
                            </div>
                            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-zinc-800">
                              {q.status !== "Approved" && (
                                <button
                                  onClick={() => handleApproveQuotation(q.id)}
                                  className="px-3 py-1.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700"
                                >
                                  Approve & Lock Pricing
                                </button>
                              )}
                              <button
                                onClick={() => handleViewQuotation(q.id)}
                                className="px-3 py-1.5 rounded-xl bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-gray-200 flex items-center gap-1 hover:bg-gray-50 dark:hover:bg-zinc-700"
                              >
                                <IconPrinter size={14} className="text-restro-green" /> View Proposal
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* New Quotation Creator Form */}
                      <form onSubmit={handleQuotationSubmit} className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 space-y-3 text-xs">
                        <h4 className="font-bold uppercase text-gray-400">Generate Quotation Line Items</h4>
                        <div className="space-y-2">
                          {quotationForm.items.map((row, idx) => (
                            <div key={idx} className="flex gap-2 items-center">
                              <select
                                value={row.item_type}
                                onChange={(e) => {
                                  const updated = [...quotationForm.items];
                                  updated[idx].item_type = e.target.value;
                                  setQuotationForm({ ...quotationForm, items: updated });
                                }}
                                className="p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                              >
                                <option value="food_beverage">Food & Beverage</option>
                                <option value="venue_rental">Venue Rental</option>
                                <option value="decoration">Decoration</option>
                                <option value="av_equipment">Audiovisual</option>
                                <option value="service">Service Staff</option>
                              </select>
                              <input
                                type="text"
                                placeholder="Description"
                                value={row.name}
                                onChange={(e) => {
                                  const updated = [...quotationForm.items];
                                  updated[idx].name = e.target.value;
                                  setQuotationForm({ ...quotationForm, items: updated });
                                }}
                                className="flex-1 p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                              />
                              <input
                                type="number"
                                placeholder="Qty"
                                value={row.quantity}
                                onChange={(e) => {
                                  const updated = [...quotationForm.items];
                                  updated[idx].quantity = e.target.value;
                                  setQuotationForm({ ...quotationForm, items: updated });
                                }}
                                className="w-16 p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                              />
                              <input
                                type="number"
                                placeholder="Rate"
                                value={row.unit_price}
                                onChange={(e) => {
                                  const updated = [...quotationForm.items];
                                  updated[idx].unit_price = e.target.value;
                                  setQuotationForm({ ...quotationForm, items: updated });
                                }}
                                className="w-24 p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                              />
                              <span className="font-black text-gray-700 dark:text-gray-300 w-24 text-right">
                                {currency} {(Number(row.quantity || 1) * Number(row.unit_price || 0)).toFixed(2)}
                              </span>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              setQuotationForm({
                                ...quotationForm,
                                items: [...quotationForm.items, { item_type: "food_beverage", name: "Additional Service", quantity: 1, unit: "Pcs", unit_price: 1000, tax_percent: 13, notes: "" }]
                              });
                            }}
                            className="text-xs font-bold text-restro-green flex items-center gap-1"
                          >
                            <IconPlus size={14} /> Add Line Item
                          </button>
                        </div>
                        <button type="submit" className="w-full py-3 rounded-xl font-bold bg-restro-green text-white">
                          Save Quotation Version
                        </button>
                      </form>
                    </div>
                  )}

                  {/* SUBTAB 3: BEO */}
                  {workspaceTab === "beo" && (
                    <form onSubmit={handleBeoSubmit} className="space-y-4 text-xs">
                      <div className="flex justify-between items-center">
                        <h4 className="font-bold text-xs uppercase text-gray-400">Banquet Event Order Specifications</h4>
                        <button type="button" onClick={() => window.print()} className="px-3 py-1.5 rounded-xl border text-xs font-bold flex items-center gap-1">
                          <IconPrinter size={14} /> Print BEO Sheet
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Theme & Color</label>
                          <input
                            type="text"
                            value={beoForm.theme_color}
                            onChange={(e) => setBeoForm({ ...beoForm, theme_color: e.target.value })}
                            className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Food Service Style</label>
                          <select
                            value={beoForm.food_service_style}
                            onChange={(e) => setBeoForm({ ...beoForm, food_service_style: e.target.value })}
                            className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                          >
                            <option value="Buffet">Buffet</option>
                            <option value="Plated / Table Service">Plated / Table Service</option>
                            <option value="Cocktail / Finger Food">Cocktail / Finger Food</option>
                            <option value="Live Counters">Live Counters</option>
                          </select>
                        </div>
                        <div className="col-span-2">
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Stage & Setup Instructions</label>
                          <input
                            type="text"
                            value={beoForm.stage_setup}
                            onChange={(e) => setBeoForm({ ...beoForm, stage_setup: e.target.value })}
                            className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Dietary Requirements</label>
                          <input
                            type="text"
                            value={beoForm.dietary_requirements}
                            onChange={(e) => setBeoForm({ ...beoForm, dietary_requirements: e.target.value })}
                            className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                          />
                        </div>
                      </div>

                      {/* Menu Courses */}
                      <div className="space-y-3 border-t border-gray-100 dark:border-zinc-800 pt-3">
                        <label className="block text-[10px] font-bold text-gray-400 uppercase">Menu Courses & Service Timings</label>
                        {beoForm.menus.map((m, idx) => (
                          <div key={idx} className="p-3 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                placeholder="Course (e.g. Appetizers)"
                                value={m.course_name}
                                onChange={(e) => {
                                  const updated = [...beoForm.menus];
                                  updated[idx].course_name = e.target.value;
                                  setBeoForm({ ...beoForm, menus: updated });
                                }}
                                className="p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                              />
                              <input
                                type="text"
                                placeholder="Timing (e.g. 07:00 PM - 08:30 PM)"
                                value={m.service_time}
                                onChange={(e) => {
                                  const updated = [...beoForm.menus];
                                  updated[idx].service_time = e.target.value;
                                  setBeoForm({ ...beoForm, menus: updated });
                                }}
                                className="p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                              />
                            </div>
                            <input
                              type="text"
                              placeholder="Dishes list (comma separated)"
                              value={m.menu_items}
                              onChange={(e) => {
                                const updated = [...beoForm.menus];
                                updated[idx].menu_items = e.target.value;
                                setBeoForm({ ...beoForm, menus: updated });
                              }}
                              className="w-full p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                            />
                          </div>
                        ))}
                      </div>

                      <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green text-white">
                        Save Banquet Event Order (BEO)
                      </button>
                    </form>
                  )}

                  {/* SUBTAB 4: DEPARTMENT TASKS */}
                  {workspaceTab === "tasks" && (
                    <div className="space-y-4 text-xs">
                      <div className="flex justify-between items-center">
                        <h4 className="font-bold text-xs uppercase text-gray-400">Department Operational Assignments</h4>
                        <button
                          onClick={async () => {
                            const res = await eventsApi.issueEventLinen(selectedEventId, {
                              venue_name: eventDetails.venue_name || "Banquet Hall",
                              event_number: eventDetails.event_number,
                              items: [{ item_id: 1, name: "Tablecloths", quantity: 20 }, { item_id: 2, name: "Napkins", quantity: 100 }]
                            });
                            toast.success(res.message);
                            reloadWorkspace();
                          }}
                          className="px-3 py-1.5 rounded-xl bg-purple-600 text-white font-bold flex items-center gap-1"
                        >
                          <IconHanger size={14} /> Issue Linen from Laundry
                        </button>
                      </div>

                      <div className="space-y-2">
                        {(eventDetails.tasks || []).map(t => (
                          <div key={t.id} className="p-3 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 flex justify-between items-center">
                            <div>
                              <span className="font-bold text-gray-900 dark:text-white">{t.task_name}</span>
                              <div className="text-[11px] text-gray-400">Dept: <strong>{t.department}</strong> • {t.description}</div>
                            </div>
                            <button
                              onClick={() => handleTaskToggle(t.id, t.status)}
                              className={`px-3 py-1 rounded-xl text-xs font-bold ${
                                t.status === "Completed" ? "bg-emerald-100 text-emerald-800" : "bg-gray-200 text-gray-700"
                              }`}
                            >
                              {t.status === "Completed" ? "✓ Done" : "Mark Done"}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* SUBTAB 5: LIVE CHECKLIST */}
                  {workspaceTab === "checklist" && (
                    <div className="space-y-3 text-xs">
                      <h4 className="font-bold text-xs uppercase text-gray-400">Live Function Readiness Checklist</h4>
                      <div className="space-y-2">
                        {(eventDetails.checklist || []).map(chk => (
                          <div
                            key={chk.id}
                            onClick={() => handleChecklistToggle(chk.id, Boolean(chk.is_completed))}
                            className="p-3 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 flex items-center gap-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-800 transition"
                          >
                            <input
                              type="checkbox"
                              checked={Boolean(chk.is_completed)}
                              onChange={() => {}}
                              className="rounded text-restro-green focus:ring-0"
                            />
                            <div className="flex-1">
                              <span className={`font-semibold ${chk.is_completed ? "line-through text-gray-400" : "text-gray-900 dark:text-white"}`}>
                                {chk.item_name}
                              </span>
                              <span className="block text-[10px] text-gray-400 capitalize">Phase: {chk.phase.replace('_', ' ')}</span>
                            </div>
                            {chk.is_completed ? (
                              <span className="text-[10px] text-emerald-600 font-bold">✓ Verified</span>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* SUBTAB 6: PAYMENTS & BILLING */}
                  {workspaceTab === "payments" && (
                    <div className="space-y-6 text-xs">
                      {/* Financial Balances Card */}
                      <div className="grid grid-cols-3 gap-3 p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900">
                        <div>
                          <span className="text-[10px] uppercase font-bold text-emerald-700">Net Event Bill</span>
                          <div className="text-xl font-black text-emerald-900 dark:text-emerald-300 mt-0.5">
                            {currency} {Number(eventDetails.net_total_amount || 0).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold text-emerald-700">Total Paid</span>
                          <div className="text-xl font-black text-emerald-900 dark:text-emerald-300 mt-0.5">
                            {currency} {Number(eventDetails.total_paid_amount || 0).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold text-rose-700">Balance Due</span>
                          <div className="text-xl font-black text-rose-700 dark:text-rose-400 mt-0.5">
                            {currency} {Number(eventDetails.balance_due_amount || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>

                      {/* Record Advance / Payment Form */}
                      <form onSubmit={handlePaymentSubmit} className="p-4 rounded-2xl bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 space-y-3">
                        <h4 className="font-bold uppercase text-gray-400">Record Advance Deposit / Payment Receipt</h4>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Amount ({currency})</label>
                            <input
                              type="number"
                              step="0.01"
                              min="1"
                              required
                              value={paymentForm.amount}
                              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                              className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Payment Type</label>
                            <select
                              value={paymentForm.payment_type}
                              onChange={(e) => setPaymentForm({ ...paymentForm, payment_type: e.target.value })}
                              className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                            >
                              <option value="advance_deposit">Advance Deposit</option>
                              <option value="milestone_payment">Milestone Installment</option>
                              <option value="final_settlement">Final Settlement</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Payment Method</label>
                            <select
                              value={paymentForm.payment_method}
                              onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                              className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                            >
                              <option value="cash">Cash</option>
                              <option value="card">Credit / Debit Card</option>
                              <option value="bank_transfer">Bank Transfer / UPI</option>
                              <option value="cheque">Cheque</option>
                            </select>
                          </div>
                        </div>
                        <button type="submit" className="w-full py-3 rounded-xl font-bold bg-restro-green text-white">
                          Record Payment Receipt
                        </button>
                      </form>

                      {/* Official Final Invoice Generation */}
                      <div className="p-4 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 flex justify-between items-center">
                        <div>
                          <p className="font-bold text-gray-900 dark:text-white">Official Accounts Invoice</p>
                          <p className="text-[11px] text-gray-400">Post event bill into General Ledger and Invoices directory</p>
                        </div>
                        {eventDetails.invoice_id ? (
                          <span className="px-3 py-1.5 rounded-xl bg-emerald-100 text-emerald-800 font-bold">
                            ✓ Invoiced in Accounts
                          </span>
                        ) : (
                          <button
                            onClick={handleGenerateInvoice}
                            className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition"
                          >
                            Generate Official Invoice
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* SUBTAB 7: AMENDMENTS AUDIT */}
                  {workspaceTab === "amendments" && (
                    <div className="space-y-3 text-xs">
                      <h4 className="font-bold text-xs uppercase text-gray-400">Post-Confirmation Amendments Audit Trail</h4>
                      <div className="space-y-2">
                        {(eventDetails.amendments || []).map(am => (
                          <div key={am.id} className="p-3 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800">
                            <div className="flex justify-between items-center font-bold">
                              <span>Field Changed: {am.field_changed}</span>
                              <span className="text-gray-400 text-[10px]">{new Date(am.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-gray-500 text-[11px] mt-0.5">
                              Old: <span className="line-through">{am.old_value || 'None'}</span> → New: <strong>{am.new_value}</strong>
                            </p>
                            <p className="text-gray-400 text-[10px] italic">Reason: {am.change_reason} • By: {am.changed_by}</p>
                          </div>
                        ))}
                        {(!eventDetails.amendments || eventDetails.amendments.length === 0) && (
                          <p className="text-center py-6 text-gray-400">No revisions recorded after booking confirmation.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* MODAL: NEW INQUIRY */}
              {modalType === "new_inquiry" && (
                <form onSubmit={handleInquirySubmit} className="space-y-4 text-xs md:text-sm">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Event Title *</label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Sharma-Verma Wedding Reception, Tech Developer Summit"
                        value={inquiryForm.event_title}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, event_title: e.target.value })}
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Customer Name *</label>
                      <input
                        type="text"
                        required
                        placeholder="Customer / Host Name"
                        value={inquiryForm.customer_name}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, customer_name: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-semibold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Customer Phone *</label>
                      <input
                        type="text"
                        required
                        placeholder="Contact number"
                        value={inquiryForm.customer_phone}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, customer_phone: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-semibold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Event Type</label>
                      <select
                        value={inquiryForm.event_type}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, event_type: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      >
                        <option value="Wedding">Wedding</option>
                        <option value="Reception">Reception</option>
                        <option value="Conference">Conference</option>
                        <option value="Seminar">Seminar</option>
                        <option value="Birthday">Birthday</option>
                        <option value="Corporate Function">Corporate Function</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Primary Venue</label>
                      <select
                        value={inquiryForm.primary_venue_id}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, primary_venue_id: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      >
                        <option value="">Select Venue...</option>
                        {venues.map(v => (
                          <option key={v.id} value={v.id}>{v.name} ({v.capacity_min}-{v.capacity_max} pax)</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Start Date & Time *</label>
                      <input
                        type="datetime-local"
                        required
                        value={inquiryForm.start_date}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, start_date: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">End Date & Time *</label>
                      <input
                        type="datetime-local"
                        required
                        value={inquiryForm.end_date}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, end_date: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Expected Guests</label>
                      <input
                        type="number"
                        min="1"
                        value={inquiryForm.expected_guests}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, expected_guests: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Seating Layout</label>
                      <select
                        value={inquiryForm.seating_layout}
                        onChange={(e) => setInquiryForm({ ...inquiryForm, seating_layout: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                      >
                        <option value="Round Table">Round Table</option>
                        <option value="Theater">Theater</option>
                        <option value="Classroom">Classroom</option>
                        <option value="U-Shape">U-Shape</option>
                        <option value="Cocktail">Standing Cocktail</option>
                      </select>
                    </div>
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green text-white mt-2">
                    Record Inquiry & Open Workspace
                  </button>
                </form>
              )}

              {/* MODAL: CHECK AVAILABILITY */}
              {modalType === "check_availability" && (
                <form onSubmit={handleCheckAvailability} className="space-y-4 text-xs md:text-sm">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Select Venue *</label>
                    <select
                      required
                      value={availabilityForm.venue_id}
                      onChange={(e) => setAvailabilityForm({ ...availabilityForm, venue_id: e.target.value })}
                      className="w-full p-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                    >
                      <option value="">Select Venue to Test...</option>
                      {venues.map(v => (
                        <option key={v.id} value={v.id}>{v.name} ({v.venue_type})</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Function Start Time *</label>
                      <input
                        type="datetime-local"
                        required
                        value={availabilityForm.start_time}
                        onChange={(e) => setAvailabilityForm({ ...availabilityForm, start_time: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Function End Time *</label>
                      <input
                        type="datetime-local"
                        required
                        value={availabilityForm.end_time}
                        onChange={(e) => setAvailabilityForm({ ...availabilityForm, end_time: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>
                  </div>

                  <button type="submit" className="w-full py-3 rounded-xl font-bold bg-restro-green text-white">
                    Execute Conflict & Buffer Check
                  </button>

                  {availabilityResult && (
                    <div className={`p-4 rounded-2xl border ${
                      availabilityResult.available
                        ? "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 text-emerald-800 dark:text-emerald-300"
                        : "bg-rose-50 dark:bg-rose-950/40 border-rose-300 text-rose-800 dark:text-rose-300"
                    }`}>
                      <p className="font-bold text-sm">
                        {availabilityResult.available ? "✓ Available for Exclusive Booking" : "✕ Venue Conflict Detected!"}
                      </p>
                      <p className="text-xs mt-1">
                        Effective window (with {availabilityResult.buffer_window?.setup_buffer_mins || 60}m setup & {availabilityResult.buffer_window?.teardown_buffer_mins || 60}m teardown buffers):
                        <br />
                        {availabilityResult.buffer_window?.effective_start} to {availabilityResult.buffer_window?.effective_end}
                      </p>
                    </div>
                  )}
                </form>
              )}

              {/* MODAL: ADD VENUE */}
              {modalType === "add_venue" && (
                <form onSubmit={handleVenueSubmit} className="space-y-4 text-xs">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Venue Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Sapphire Ballroom, Rooftop Lawn"
                      value={venueForm.name}
                      onChange={(e) => setVenueForm({ ...venueForm, name: e.target.value })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Venue Type</label>
                      <select
                        value={venueForm.venue_type}
                        onChange={(e) => setVenueForm({ ...venueForm, venue_type: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                      >
                        <option value="Banquet Hall">Banquet Hall</option>
                        <option value="Ballroom">Ballroom</option>
                        <option value="Lawn / Garden">Lawn / Garden</option>
                        <option value="Conference Hall">Conference Hall</option>
                        <option value="Poolside">Poolside</option>
                        <option value="Rooftop">Rooftop</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Capacity (Min - Max)</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          placeholder="Min"
                          value={venueForm.capacity_min}
                          onChange={(e) => setVenueForm({ ...venueForm, capacity_min: e.target.value })}
                          className="w-1/2 p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        />
                        <input
                          type="number"
                          placeholder="Max"
                          value={venueForm.capacity_max}
                          onChange={(e) => setVenueForm({ ...venueForm, capacity_max: e.target.value })}
                          className="w-1/2 p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Daily Rental ({currency})</label>
                      <input
                        type="number"
                        min="0"
                        value={venueForm.rental_rate_per_day}
                        onChange={(e) => setVenueForm({ ...venueForm, rental_rate_per_day: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Hourly Rental ({currency})</label>
                      <input
                        type="number"
                        min="0"
                        value={venueForm.rental_rate_per_hour}
                        onChange={(e) => setVenueForm({ ...venueForm, rental_rate_per_hour: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green text-white">
                    Save Venue
                  </button>
                </form>
              )}

              {/* MODAL: ADD PACKAGE */}
              {modalType === "add_package" && (
                <form onSubmit={handlePackageSubmit} className="space-y-4 text-xs">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Package Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Royal Wedding Feast, Executive Seminar Package"
                      value={packageForm.name}
                      onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                      className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-sm font-bold"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Base Price ({currency})</label>
                      <input
                        type="number"
                        min="0"
                        value={packageForm.base_price}
                        onChange={(e) => setPackageForm({ ...packageForm, base_price: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Min Guests</label>
                      <input
                        type="number"
                        min="1"
                        value={packageForm.min_guests}
                        onChange={(e) => setPackageForm({ ...packageForm, min_guests: e.target.value })}
                        className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                      />
                    </div>
                  </div>

                  <button type="submit" className="w-full py-3.5 rounded-xl font-bold bg-restro-green text-white">
                    Save Package
                  </button>
                </form>
              )}

              {/* MODAL: CREATE BANQUET QUOTATION PROPOSAL */}
              {modalType === "create_quotation" && (
                <form onSubmit={(e) => handleDirectQuotationSubmit(e, false)} className="space-y-6 text-xs">
                  {/* Optional Link to Existing Event */}
                  <div className="p-4 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 space-y-2">
                    <label className="block text-xs font-bold text-gray-500 uppercase">
                      Link to Existing Event Inquiry (Optional)
                    </label>
                    <select
                      value={directQuotationForm.event_id || ""}
                      onChange={(e) => {
                        const evId = e.target.value;
                        if (!evId) {
                          setDirectQuotationForm(prev => ({ ...prev, event_id: "" }));
                          return;
                        }
                        const selEv = eventsList.find(ev => String(ev.id) === String(evId));
                        if (selEv) {
                          setDirectQuotationForm(prev => ({
                            ...prev,
                            event_id: selEv.id,
                            customer_name: selEv.customer_name || prev.customer_name,
                            customer_phone: selEv.customer_phone || prev.customer_phone,
                            customer_email: selEv.customer_email || prev.customer_email,
                            customer_company: selEv.customer_company || prev.customer_company,
                            customer_address: selEv.customer_address || prev.customer_address,
                            customer_pan: selEv.customer_pan || prev.customer_pan,
                            event_title: selEv.event_title || prev.event_title,
                            event_type: selEv.event_type || prev.event_type,
                            primary_venue_id: selEv.primary_venue_id || prev.primary_venue_id,
                            package_id: selEv.package_id || prev.package_id,
                            guest_count: selEv.guaranteed_guests || selEv.expected_guests || prev.guest_count,
                            start_date: selEv.start_date ? new Date(selEv.start_date).toISOString().slice(0, 16) : prev.start_date,
                            end_date: selEv.end_date ? new Date(selEv.end_date).toISOString().slice(0, 16) : prev.end_date
                          }));
                        }
                      }}
                      className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                    >
                      <option value="">-- Create Standalone Proposal (Auto-creates Event Inquiry) --</option>
                      {eventsList.map(ev => (
                        <option key={ev.id} value={ev.id}>
                          {ev.event_number} - {ev.event_title} ({ev.customer_name})
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-gray-400">
                      If you select an existing event, this quotation will be added directly to its proposal versions. If omitted, a new Inquiry lead is created automatically.
                    </p>
                  </div>

                  {/* Customer Information */}
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Customer & Contact Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Customer Name *</label>
                        <input
                          type="text"
                          required
                          placeholder="Full Name"
                          value={directQuotationForm.customer_name}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, customer_name: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Mobile Phone *</label>
                        <input
                          type="tel"
                          required
                          placeholder="98xxxxxxxx"
                          value={directQuotationForm.customer_phone}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, customer_phone: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Email Address</label>
                        <input
                          type="email"
                          placeholder="client@example.com"
                          value={directQuotationForm.customer_email}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, customer_email: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Company / Organization</label>
                        <input
                          type="text"
                          placeholder="Company or Family Name"
                          value={directQuotationForm.customer_company}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, customer_company: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">PAN / VAT No</label>
                        <input
                          type="text"
                          placeholder="Tax Identification"
                          value={directQuotationForm.customer_pan}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, customer_pan: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Billing Address</label>
                        <input
                          type="text"
                          placeholder="City, District"
                          value={directQuotationForm.customer_address}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, customer_address: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Event Specifications */}
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Event Specifications & Venue</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Event Title *</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Adhikari Wedding Reception"
                          value={directQuotationForm.event_title}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, event_title: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Event Type</label>
                        <select
                          value={directQuotationForm.event_type}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, event_type: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        >
                          <option value="Wedding">Wedding</option>
                          <option value="Reception">Reception</option>
                          <option value="Conference">Conference</option>
                          <option value="Seminar">Seminar</option>
                          <option value="Birthday">Birthday</option>
                          <option value="Corporate Function">Corporate Function</option>
                          <option value="Anniversary">Anniversary</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Primary Venue / Hall</label>
                        <select
                          value={directQuotationForm.primary_venue_id}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, primary_venue_id: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        >
                          <option value="">-- Select Venue (Optional) --</option>
                          {venues.map(v => (
                            <option key={v.id} value={v.id}>{v.name} ({v.venue_type} • Max {v.capacity_max} pax)</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Banquet Menu Package</label>
                        <select
                          value={directQuotationForm.package_id}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, package_id: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        >
                          <option value="">-- Select Package (Optional) --</option>
                          {packages.map(p => (
                            <option key={p.id} value={p.id}>{p.name} ({currency} {p.base_price}/pax)</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Guaranteed Guest Count</label>
                        <input
                          type="number"
                          min="1"
                          value={directQuotationForm.guest_count}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, guest_count: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Seating Layout</label>
                        <select
                          value={directQuotationForm.seating_layout}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, seating_layout: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        >
                          <option value="Round Table">Round Table / Banquet Style</option>
                          <option value="Theater Style">Theater Style</option>
                          <option value="Classroom Style">Classroom Style</option>
                          <option value="U-Shape">U-Shape</option>
                          <option value="Cluster">Cluster</option>
                          <option value="Standing Cocktail">Standing Cocktail</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Start Date & Time</label>
                        <input
                          type="datetime-local"
                          value={directQuotationForm.start_date}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, start_date: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">End Date & Time</label>
                        <input
                          type="datetime-local"
                          value={directQuotationForm.end_date}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, end_date: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase mb-1">Proposal Valid Until</label>
                        <input
                          type="date"
                          value={directQuotationForm.valid_until}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, valid_until: e.target.value })}
                          className="w-full p-2.5 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-medium"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Line Items Builder */}
                  <div>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-2">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500">Quotation Line Items & Pricing Breakdown</h4>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={handleAddPackageMenuItem}
                          className="px-2.5 py-1.5 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300 font-bold text-[11px] flex items-center gap-1 border border-purple-200 dark:border-purple-800 transition"
                        >
                          <IconPlus size={13} /> + Package Menu
                        </button>
                        <button
                          type="button"
                          onClick={handleAddVenueRentalItem}
                          className="px-2.5 py-1.5 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 font-bold text-[11px] flex items-center gap-1 border border-blue-200 dark:border-blue-800 transition"
                        >
                          <IconPlus size={13} /> + Venue Rental
                        </button>
                        <button
                          type="button"
                          onClick={handleAddCustomItem}
                          className="px-2.5 py-1.5 rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 font-bold text-[11px] flex items-center gap-1 transition"
                        >
                          <IconPlus size={13} /> + Custom Item
                        </button>
                      </div>
                    </div>

                    <div className="border border-gray-200 dark:border-zinc-800 rounded-2xl overflow-hidden">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-gray-50 dark:bg-zinc-800/80 text-gray-500 uppercase text-[10px] font-bold border-b border-gray-200 dark:border-zinc-800">
                          <tr>
                            <th className="p-2.5">Category</th>
                            <th className="p-2.5">Item Description</th>
                            <th className="p-2.5 w-20 text-center">Qty</th>
                            <th className="p-2.5 w-20 text-center">Unit</th>
                            <th className="p-2.5 w-28 text-right">Unit Rate ({currency})</th>
                            <th className="p-2.5 w-28 text-right">Line Total ({currency})</th>
                            <th className="p-2.5 w-10 text-center"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-zinc-800/60">
                          {directQuotationForm.items.map((it, idx) => (
                            <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/30">
                              <td className="p-2">
                                <select
                                  value={it.item_type}
                                  onChange={(e) => handleUpdateItem(idx, "item_type", e.target.value)}
                                  className="p-1.5 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent text-xs w-full"
                                >
                                  <option value="food_beverage">Food & Beverage</option>
                                  <option value="venue_rental">Venue Rental</option>
                                  <option value="decoration">Decoration</option>
                                  <option value="av_equipment">Audiovisual</option>
                                  <option value="service">Service Staff</option>
                                  <option value="other">Other / Custom</option>
                                </select>
                              </td>
                              <td className="p-2">
                                <input
                                  type="text"
                                  required
                                  placeholder="Item name / specification"
                                  value={it.name}
                                  onChange={(e) => handleUpdateItem(idx, "name", e.target.value)}
                                  className="w-full p-1.5 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-medium"
                                />
                              </td>
                              <td className="p-2">
                                <input
                                  type="number"
                                  min="1"
                                  value={it.quantity}
                                  onChange={(e) => handleUpdateItem(idx, "quantity", e.target.value)}
                                  className="w-full p-1.5 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent text-xs text-center font-bold"
                                />
                              </td>
                              <td className="p-2">
                                <input
                                  type="text"
                                  placeholder="Pax/Day"
                                  value={it.unit}
                                  onChange={(e) => handleUpdateItem(idx, "unit", e.target.value)}
                                  className="w-full p-1.5 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent text-xs text-center"
                                />
                              </td>
                              <td className="p-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={it.unit_price}
                                  onChange={(e) => handleUpdateItem(idx, "unit_price", e.target.value)}
                                  className="w-full p-1.5 rounded-lg border border-gray-200 dark:border-zinc-700 bg-transparent text-xs text-right font-bold"
                                />
                              </td>
                              <td className="p-2 text-right font-black text-gray-900 dark:text-white">
                                {((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)).toFixed(2)}
                              </td>
                              <td className="p-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => handleRemoveItem(idx)}
                                  className="p-1 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                                  title="Remove item"
                                >
                                  <IconTrash size={15} />
                                </button>
                              </td>
                            </tr>
                          ))}
                          {directQuotationForm.items.length === 0 && (
                            <tr>
                              <td colSpan={7} className="p-4 text-center text-gray-400">
                                No line items added yet. Click "+ Package Menu" or "+ Venue Rental" to populate.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Financial Adjustments & Calculated Totals */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800">
                    <div className="space-y-3">
                      <h4 className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Taxes, Discounts & Deposits</h4>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Discount %</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={directQuotationForm.discount_percent}
                            onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, discount_percent: e.target.value })}
                            className="w-full p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">VAT / Tax %</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={directQuotationForm.tax_percent}
                            onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, tax_percent: e.target.value })}
                            className="w-full p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Advance Req %</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={directQuotationForm.advance_required_percent}
                            onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, advance_required_percent: e.target.value })}
                            className="w-full p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs font-bold"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Terms & Conditions</label>
                        <textarea
                          rows="3"
                          value={directQuotationForm.terms_conditions}
                          onChange={(e) => setDirectQuotationForm({ ...directQuotationForm, terms_conditions: e.target.value })}
                          className="w-full p-2 rounded-xl border border-gray-200 dark:border-zinc-700 bg-transparent text-xs leading-relaxed"
                        ></textarea>
                      </div>
                    </div>

                    <div className="flex flex-col justify-between space-y-2 p-3 bg-white dark:bg-zinc-800/60 rounded-xl border border-gray-100 dark:border-zinc-700">
                      <div className="space-y-1.5 text-xs">
                        <div className="flex justify-between text-gray-500">
                          <span>Items Subtotal:</span>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {currency} {directQuotationCalculations.subtotal.toFixed(2)}
                          </span>
                        </div>
                        {directQuotationCalculations.discountAmount > 0 && (
                          <div className="flex justify-between text-emerald-600">
                            <span>Discount ({directQuotationForm.discount_percent}%):</span>
                            <span className="font-bold">
                              -{currency} {directQuotationCalculations.discountAmount.toFixed(2)}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between text-gray-500">
                          <span>Taxable Total:</span>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {currency} {directQuotationCalculations.taxable.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-gray-500">
                          <span>Tax ({directQuotationForm.tax_percent}%):</span>
                          <span className="font-bold text-gray-900 dark:text-white">
                            {currency} {directQuotationCalculations.taxAmount.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between py-2 border-t border-b border-gray-200 dark:border-zinc-700 text-sm font-black text-gray-900 dark:text-white">
                          <span>Grand Total:</span>
                          <span className="text-restro-green">
                            {currency} {directQuotationCalculations.grandTotal.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-purple-600 font-bold">
                          <span>Advance Required ({directQuotationForm.advance_required_percent}%):</span>
                          <span>
                            {currency} {directQuotationCalculations.advanceAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <button
                          type="submit"
                          className="flex-1 py-2.5 rounded-xl bg-gray-900 dark:bg-zinc-100 text-white dark:text-gray-900 font-bold text-xs hover:bg-gray-800 transition"
                        >
                          Save as Draft
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleDirectQuotationSubmit(e, true)}
                          className="flex-1 py-2.5 rounded-xl bg-restro-green hover:bg-restro-green-button-hover text-white font-bold text-xs transition shadow-sm"
                        >
                          Save & Approve
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}

              {/* MODAL: VIEW / PRINT BANQUET QUOTATION PROPOSAL */}
              {modalType === "view_quotation" && viewingQuotation && (
                <div className="space-y-6">
                  {/* Action Bar (Not Printed) */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 p-4 rounded-2xl bg-gray-50 dark:bg-zinc-900 border border-gray-100 dark:border-zinc-800 no-print">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold text-gray-500">{viewingQuotation.quotation_number}</span>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${getStatusBadge(viewingQuotation.status)}`}>
                        {viewingQuotation.status}
                      </span>
                      {viewingQuotation.version && (
                        <span className="text-xs text-gray-400 font-medium">Version {viewingQuotation.version}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {viewingQuotation.status !== "Approved" && (
                        <button
                          onClick={() => handleApproveQuotationDirect(viewingQuotation.id, viewingQuotation.event_id)}
                          className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition"
                        >
                          <IconCheck size={16} /> Approve & Lock Pricing
                        </button>
                      )}
                      <button
                        onClick={() => window.print()}
                        className="px-4 py-2 rounded-xl bg-restro-green hover:bg-restro-green-button-hover text-white font-bold text-xs flex items-center gap-1.5 shadow-sm transition"
                      >
                        <IconPrinter size={16} /> Print / Download PDF
                      </button>
                    </div>
                  </div>

                  {/* Formal Printable Document */}
                  <div className="printable-proposal bg-white dark:bg-zinc-900 text-gray-900 dark:text-gray-100 p-6 md:p-8 rounded-3xl border border-gray-200 dark:border-zinc-800 shadow-sm space-y-6">
                    {/* Print CSS rules embedded for seamless print dialog formatting */}
                    <style>{`
                      @media print {
                        body * {
                          visibility: hidden !important;
                        }
                        .printable-proposal, .printable-proposal * {
                          visibility: visible !important;
                        }
                        .printable-proposal {
                          position: absolute !important;
                          left: 0 !important;
                          top: 0 !important;
                          width: 100% !important;
                          margin: 0 !important;
                          padding: 24px !important;
                          background: white !important;
                          color: #111827 !important;
                          border: none !important;
                          box-shadow: none !important;
                        }
                        .no-print {
                          display: none !important;
                        }
                      }
                    `}</style>

                    {/* Hotel & Proposal Header */}
                    <div className="flex flex-col sm:flex-row justify-between items-start border-b border-gray-200 dark:border-zinc-800 pb-5 gap-4">
                      <div>
                        <h2 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight">
                          {storeSettings?.store_name || "eHospitality Banquet & Events"}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {storeSettings?.address || "Hospitality Plaza, Banquet Division"}
                        </p>
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3 gap-y-1 mt-1">
                          {storeSettings?.phone && <span>Tel: {storeSettings.phone}</span>}
                          {storeSettings?.email && <span>Email: {storeSettings.email}</span>}
                          {storeSettings?.pan_no && <span>PAN / VAT: <strong>{storeSettings.pan_no}</strong></span>}
                        </div>
                      </div>

                      <div className="sm:text-right">
                        <span className="px-3 py-1 rounded-full text-xs font-black bg-restro-green/10 text-restro-green uppercase tracking-wider inline-block mb-2">
                          Banquet Quotation
                        </span>
                        <div className="font-mono text-sm font-bold text-gray-900 dark:text-white">
                          {viewingQuotation.quotation_number}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Date: {new Date(viewingQuotation.created_at || Date.now()).toLocaleDateString()}
                        </div>
                        {viewingQuotation.valid_until && (
                          <div className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                            Valid Until: {new Date(viewingQuotation.valid_until).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Client Details & Event Overview Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-zinc-800/50 border border-gray-100 dark:border-zinc-800 text-xs">
                      <div className="space-y-1.5">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Client Information</div>
                        <div className="font-bold text-sm text-gray-900 dark:text-white">{viewingQuotation.customer_name}</div>
                        {viewingQuotation.customer_company && (
                          <div className="text-gray-600 dark:text-gray-300 font-medium">Organization: {viewingQuotation.customer_company}</div>
                        )}
                        <div className="text-gray-500">Phone: {viewingQuotation.customer_phone}</div>
                        {viewingQuotation.customer_email && <div className="text-gray-500">Email: {viewingQuotation.customer_email}</div>}
                        {viewingQuotation.customer_address && <div className="text-gray-500">Address: {viewingQuotation.customer_address}</div>}
                        {viewingQuotation.customer_pan && <div className="text-gray-500">Tax/PAN: {viewingQuotation.customer_pan}</div>}
                      </div>

                      <div className="space-y-1.5 md:border-l md:border-gray-200 dark:md:border-zinc-700 md:pl-4">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Event Specifications</div>
                        <div className="font-bold text-sm text-purple-600">{viewingQuotation.event_title}</div>
                        <div className="text-gray-600 dark:text-gray-300">
                          Type: <span className="font-bold capitalize">{viewingQuotation.event_type}</span> • Venue: <span className="font-bold">{viewingQuotation.venue_name || "TBD"}</span>
                        </div>
                        <div className="text-gray-500">
                          Date: {viewingQuotation.start_date ? new Date(viewingQuotation.start_date).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : "TBD"}
                        </div>
                        <div className="text-gray-500">
                          Guaranteed Guests: <strong className="text-gray-800 dark:text-gray-200">{viewingQuotation.guest_count} pax</strong>
                        </div>
                        {viewingQuotation.package_name && (
                          <div className="text-gray-500">
                            Selected Menu: <strong className="text-gray-800 dark:text-gray-200">{viewingQuotation.package_name}</strong>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Itemized Line Items Table */}
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Itemized Banquet & Event Services</h4>
                      <div className="border border-gray-200 dark:border-zinc-800 rounded-2xl overflow-hidden">
                        <table className="w-full text-left text-xs">
                          <thead className="bg-gray-50 dark:bg-zinc-800/80 text-gray-600 dark:text-gray-300 font-bold border-b border-gray-200 dark:border-zinc-800">
                            <tr>
                              <th className="p-3 w-10 text-center">#</th>
                              <th className="p-3">Category</th>
                              <th className="p-3">Item Description</th>
                              <th className="p-3 text-center">Qty</th>
                              <th className="p-3 text-center">Unit</th>
                              <th className="p-3 text-right">Unit Rate ({currency})</th>
                              <th className="p-3 text-right">Amount ({currency})</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                            {(viewingQuotation.items || []).map((it, idx) => (
                              <tr key={it.id || idx} className="hover:bg-gray-50/50 dark:hover:bg-zinc-800/40">
                                <td className="p-3 text-center text-gray-400 font-mono">{idx + 1}</td>
                                <td className="p-3">
                                  <span className="capitalize px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-gray-300">
                                    {String(it.item_type || "").replace(/_/g, " ")}
                                  </span>
                                </td>
                                <td className="p-3">
                                  <div className="font-bold text-gray-900 dark:text-white">{it.name}</div>
                                  {it.notes && <div className="text-[11px] text-gray-400">{it.notes}</div>}
                                </td>
                                <td className="p-3 text-center font-bold">{it.quantity}</td>
                                <td className="p-3 text-center text-gray-500">{it.unit || "Unit"}</td>
                                <td className="p-3 text-right font-medium">{Number(it.unit_price).toFixed(2)}</td>
                                <td className="p-3 text-right font-bold text-gray-900 dark:text-white">
                                  {Number(it.subtotal || (Number(it.quantity) * Number(it.unit_price))).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                            {(!viewingQuotation.items || viewingQuotation.items.length === 0) && (
                              <tr>
                                <td colSpan={7} className="p-6 text-center text-gray-400">
                                  No individual items specified in this quotation.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Financial Summary & Breakdown */}
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pt-2">
                      <div className="flex-1 space-y-2 text-xs text-gray-500">
                        <div className="font-bold uppercase tracking-wider text-gray-400 text-[10px]">Payment & Advance Policy</div>
                        <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-950/40 border border-purple-100 dark:border-purple-900/60 text-purple-900 dark:text-purple-300">
                          A confirmation deposit of <strong>{viewingQuotation.advance_required_percent || 25}%</strong> ({currency} {Number(viewingQuotation.advance_required_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) is required to reserve the banquet hall and lock date availability.
                        </div>
                      </div>

                      <div className="w-full sm:w-72 space-y-2 text-xs">
                        <div className="flex justify-between py-1 text-gray-600 dark:text-gray-400">
                          <span>Subtotal:</span>
                          <span className="font-medium">{currency} {Number(viewingQuotation.subtotal || 0).toFixed(2)}</span>
                        </div>
                        {Number(viewingQuotation.discount_amount || 0) > 0 && (
                          <div className="flex justify-between py-1 text-emerald-600">
                            <span>Discount ({viewingQuotation.discount_percent || 0}%):</span>
                            <span className="font-medium">-{currency} {Number(viewingQuotation.discount_amount).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between py-1 text-gray-600 dark:text-gray-400">
                          <span>Tax / VAT ({viewingQuotation.tax_percent || 13}%):</span>
                          <span className="font-medium">{currency} {Number(viewingQuotation.tax_amount || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-t border-b border-gray-200 dark:border-zinc-800 text-sm font-black text-gray-900 dark:text-white">
                          <span>Grand Total:</span>
                          <span className="text-restro-green">{currency} {Number(viewingQuotation.grand_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between py-1 font-bold text-purple-600">
                          <span>Advance Required:</span>
                          <span>{currency} {Number(viewingQuotation.advance_required_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>

                    {/* Terms and Conditions */}
                    {viewingQuotation.terms_conditions && (
                      <div className="pt-3 border-t border-gray-100 dark:border-zinc-800 text-xs">
                        <div className="font-bold uppercase tracking-wider text-gray-400 text-[10px] mb-1">Terms & Conditions</div>
                        <p className="text-gray-600 dark:text-gray-400 whitespace-pre-line leading-relaxed">
                          {viewingQuotation.terms_conditions}
                        </p>
                      </div>
                    )}

                    {/* Signature Authorization Block */}
                    <div className="grid grid-cols-2 gap-8 pt-10 border-t border-gray-200 dark:border-zinc-800 text-xs">
                      <div className="text-center space-y-1">
                        <div className="border-b border-gray-300 dark:border-zinc-700 w-3/4 mx-auto pb-8"></div>
                        <div className="font-bold text-gray-900 dark:text-white pt-2">Authorized Signatory</div>
                        <div className="text-[10px] text-gray-400">Banquet & Events Sales Manager</div>
                      </div>
                      <div className="text-center space-y-1">
                        <div className="border-b border-gray-300 dark:border-zinc-700 w-3/4 mx-auto pb-8"></div>
                        <div className="font-bold text-gray-900 dark:text-white pt-2">Client Acceptance</div>
                        <div className="text-[10px] text-gray-400">Sign & Date to Confirm Acceptance</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
